// O card de um score ESTIMADO — Recuperação e Prontidão.
//
// ═════════════════════════════════════════════════════════════════════════
// 🔴 O RÓTULO NÃO É OPCIONAL, E ELE VEM DO DADO
// ═════════════════════════════════════════════════════════════════════════
// `EstimatedScore.estimate` é `true` no TIPO (lib/health/recovery.ts), então
// este card não tem como renderizar o número sem poder dizer que é derivado.
// A frase de precisão fica junto do número, não escondida num "saiba mais":
// rastreador de consumo acerta 60–80% no estagiamento (J Clin Sleep Med,
// 2026), e é exatamente essa honestidade que permite entregar hoje em vez de
// esperar um sensor que já está no pulso — ou no bolso — da pessoa.
//
// ⛔ SEM DADO, NÃO SAI NÚMERO. O card diz O QUE FALTA e QUANTAS NOITES
// faltam. "--" com cadeado (o estado anterior) é a pior das opções: parece
// defeito e não ensina nada. E inventar um número seria pior ainda.

import React from "react";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import type { EstimatedScore } from "@/lib/health/recovery";

interface EstimatedScoreCardProps {
  score: EstimatedScore;
  icon: LucideIcon;
  /** Chave i18n do título ("sleep.insights.recovery" | ".readiness"). */
  titleKey: string;
  /** Chave i18n da frase de uma linha que explica O QUE o número mede. */
  subtitleKey: string;
  accent: string;
  delay?: number;
}

/** Chave i18n de cada entrada que pode faltar. */
const MISSING_KEY: Record<string, string> = {
  hrv: "sleep.scores.missing.hrv",
  resting_hr: "sleep.scores.missing.restingHr",
  sleep: "sleep.scores.missing.sleep",
  baseline: "sleep.scores.missing.baseline",
};

/** Chave i18n de cada parte que COMPÔS o número. */
const PART_KEY: Record<string, string> = {
  hrv: "sleep.scores.part.hrv",
  resting_hr: "sleep.scores.part.restingHr",
  sleep: "sleep.scores.part.sleep",
  hrv_trend: "sleep.scores.part.hrvTrend",
};

const EstimatedScoreCard: React.FC<EstimatedScoreCardProps> = ({
  score, icon: Icon, titleKey, subtitleKey, accent, delay = 0,
}) => {
  const { t, tp } = useLanguage();
  const has = score.value !== null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="rounded-2xl p-4 mb-4"
      style={{ background: "linear-gradient(145deg, hsl(220 40% 9%), hsl(225 35% 6%))", border: `1px solid ${accent}22` }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={12} style={{ color: accent }} />
        <p className="text-[9px] font-display tracking-wide uppercase" style={{ color: accent }}>
          {t(titleKey)}
        </p>
        {/* O selo de ESTIMATIVA fica na mesma linha do título, sempre — com ou
            sem número. É o que impede o card de ser lido como medição. */}
        <span
          className="ml-auto text-[7px] font-display font-bold tracking-[0.14em] px-1.5 py-0.5 rounded-full"
          style={{ background: "hsl(38 92% 55% / 0.14)", color: "hsl(38 92% 66%)", border: "1px solid hsl(38 92% 55% / 0.28)" }}
        >
          {t("sleep.scores.estimate")}
        </span>
      </div>

      {has ? (
        <>
          <div className="flex items-end gap-1 mb-1">
            <span className="font-display text-[28px] font-bold text-white leading-none">{score.value}</span>
            <span className="font-display text-[12px] mb-1" style={{ color: accent }}>/100</span>
          </div>
          <div className="w-full h-1 rounded-full mt-2 overflow-hidden" style={{ background: "hsl(218 14% 14%)" }}>
            <motion.div
              className="h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${score.value}%` }}
              transition={{ delay: delay + 0.15, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ background: accent }}
            />
          </div>
          {/* O QUE compôs o número. Card que decreta não ensina nada; este
              mostra as partes e o peso de cada uma. */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5">
            {score.parts.map((p) => (
              <span key={p.key} className="text-[9px] font-body" style={{ color: "hsl(218 14% 55%)" }}>
                {t(PART_KEY[p.key] ?? p.key)} · {Math.round(p.weight * 100)}%
              </span>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-[12px] font-body leading-snug" style={{ color: "hsl(218 14% 70%)" }}>
            {score.nightsNeeded > 0
              ? tp("sleep.scores.needNights", score.nightsNeeded)
              : t(MISSING_KEY[score.missing[0] ?? "sleep"] ?? "sleep.scores.missing.sleep")}
          </p>
          {score.nightsNeeded > 0 && score.missing[0] && (
            <p className="text-[9px] font-body mt-1" style={{ color: "hsl(218 14% 50%)" }}>
              {t(MISSING_KEY[score.missing[0]])}
            </p>
          )}
        </>
      )}

      <p className="text-[9px] font-body mt-2.5 leading-snug" style={{ color: "hsl(218 14% 46%)" }}>
        {t(subtitleKey)} · {t("sleep.scores.estimateNote")}
      </p>
    </motion.div>
  );
};

export default EstimatedScoreCard;
